import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import type { AppState } from '~/reducers';
import logger from '~/utils/logger/logger';
import {
  clearWebCommand,
  dispatchNativeCommand,
  drainAllCommands,
  setOnWebViewFalseIfUndefined,
} from '../store/reducer';
import { NativeCommandEnum, WebCommandEnum } from '../models/commands';
import { useNativeAppListener } from './use-native-app-listener';
import { useNativeAppDispatcher } from './use-native-app-dispatcher';
import { useWebviewAuth } from './use-webview-auth';
import { useInitializeWatcher } from './use-initialize-watcher';
import useMowerBleConnectionStatus from './use-mower-ble-connection-status';
import { useConsumeWebCommand } from './use-consume-web-command';
import { useMowerSitemapUpdate } from './use-mower-sitemap-update';

const STUCK_HEAD_TIMEOUT_MS = 1500;

export const useOnWebView = () => {
  const dispatch = useDispatch();
  const [initialized, setInitialized] = useState<boolean>(false);

  useNativeAppListener();
  useNativeAppDispatcher();
  useMowerBleConnectionStatus();
  useMowerSitemapUpdate();
  useWebviewAuth();
  const { initializing } = useInitializeWatcher();
  const onWebView = useSelector((state: AppState) => state.native.onWebView);
  useEffect(() => {
    if (!initializing) {
      setInitialized(true);
    }
  }, [initializing]);

  useEffect(() => {
    if (onWebView === undefined) {
      dispatch(dispatchNativeCommand({ name: NativeCommandEnum.AppLoaded }));
    }
  }, [dispatch, onWebView]);

  useConsumeWebCommand(WebCommandEnum.RequestAppLoaded, () => {
    if (!onWebView) {
      dispatch(dispatchNativeCommand({ name: NativeCommandEnum.AppLoaded }));
    }
  });

  useEffect(() => {
    setTimeout(() => {
      // If command InitializeWebView not received during 1 sec, make negative decision
      dispatch(setOnWebViewFalseIfUndefined());
    }, 1000);
  }, [dispatch]);

  // Outside a webview no consumer will ever fire — drop any commands that
  // arrive (or are still queued from before onWebView was resolved) so the
  // queue can't accumulate indefinitely.
  const queuedCount = useSelector(
    (state: AppState) =>
      (state.native.webCommands?.length ?? 0) + (state.native.nativeCommands?.length ?? 0)
  );
  useEffect(() => {
    if (onWebView === false && queuedCount > 0) {
      dispatch(drainAllCommands());
    }
  }, [dispatch, onWebView, queuedCount]);

  // Watchdog: if the head of the webCommands queue sits unconsumed past the
  // timeout (e.g. its consumer hook isn't currently mounted, or the command
  // was sent to the wrong context), drop it so subsequent commands aren't
  // blocked behind it for the rest of the session.
  const head = useSelector((state: AppState) => state.native.webCommands?.[0]);
  useEffect(() => {
    if (!head) {
      return;
    }
    const sawHead = head;
    const timer = setTimeout(() => {
      logger.warn(
        `native/webCommand "${sawHead.name}" sat unconsumed for ${STUCK_HEAD_TIMEOUT_MS}ms — draining`
      );
      dispatch(clearWebCommand());
    }, STUCK_HEAD_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [dispatch, head]);

  useEffect(() => {
    if (onWebView && initialized) {
      dispatch(dispatchNativeCommand({ name: NativeCommandEnum.AppInitialized }));
    }
  }, [dispatch, initialized, onWebView]);
};
